import { useCallback, useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { toast_error, toast_success } from '@/libs/toast-wrappers';
import { ERROR_CREATING_CASE, ERROR_MESSAGE_TRY_AGAIN_EMAIL } from '@/utils/messages';
import { fromInputDateToFormattedString } from '@/utils/dates';
import { CASE_STATUS, isCaseSubmitted } from '@/utils/cases';
import { postCase, patchCase } from '@/api/cases';
import { useUserProfile } from '@/states/user-profile';
import { useGetCatalogsCreateCase } from '@/hooks/api/use-get-catalogs-create-case';
import { useGetSiteById } from '@/hooks/api/use-get-site-by-id';
import { useGetCaseById } from '@/hooks/api/use-get-case-by-id';
import { useGetCases } from '@/hooks/api/use-get-cases';
import { requiredString } from '@/utils/zod-schemas';

import { Button } from '@/components/ui/button';
import { InputField } from '@/components/form/input-field';
import { SelectField } from '@/components/form/select-field';
import { FetchStates } from '@/components/fetch-states';

import { CaseIndicationMultiSelect } from './new-case/case-indication-multiselect';
import { LoadingDots } from '../ui/loading-dots';

const schema = z.object({
  createdBy: z.string(),
  clinicId: z.string(),
  siteId: requiredString,
  cameraDDL: requiredString,
  firstName: requiredString,
  lastName: requiredString,
  birthday: requiredString,
  indications: z.array(
    z.object({
      id: z.string(),
      indication: z.string(),
      method: z.string().optional(),
    })
  ),
});

type FormValues = z.infer<typeof schema>;

const DEFAULT_FORM_VALUES: FormValues = {
  createdBy: '',
  clinicId: '',
  siteId: '',
  cameraDDL: '',
  firstName: '',
  lastName: '',
  birthday: '',
  indications: [],
};

type CaseFormProps = {
  setNewCaseId: (newCase: string) => void;
  caseId?: string;
};

export function CaseForm({ setNewCaseId, caseId }: CaseFormProps) {
  const profile = useUserProfile(useShallow((s) => s.profile));
  const { t } = useTranslation();

  // Determine if we're in editing mode
  const isEditing = Boolean(caseId);

  // Fetch case data if editing
  const {
    loading: loadingCase,
    error: caseError,
    case: caseData,
    refetch,
  } = useGetCaseById({
    caseId: isEditing ? caseId : undefined,
  });

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: DEFAULT_FORM_VALUES,
    resolver: zodResolver(schema),
  });
  const { site, loading: loadingSite } = useGetSiteById({ site_id: watch('siteId') });
  const { catalogs, loading } = useGetCatalogsCreateCase();
  const { refresh } = useGetCases();

  const [isSaving, setIsSaving] = useState(false);

  const createCase = useCallback(
    async (caseData: FormValues) => {
      if (!caseData.firstName || !caseData.lastName || !caseData.birthday) {
        toast_error(t('case-create-patient-id-required'));
        return;
      }

      // here we are setting the patientId based on the patientName and patientBirthday
      // format we use: {patientName}_{patientBirthday} i.e. John Doe_01-20-1990 // MM/DD/YYYY
      const formattedBirthday = fromInputDateToFormattedString(caseData.birthday);
      const patientName = `${caseData.firstName.trim()} ${caseData.lastName.trim()}`;
      const patientId = `${patientName}_${formattedBirthday}`;

      const modelCaseData = {
        patient_id: patientId,
        patientName: patientName,
        site_id: caseData.siteId,
        camera: caseData.cameraDDL,
        createdBy: profile.email,
        orderType: 'TH' as const,
        indications: caseData.indications,
      };

      // Remove empty fields
      Object.keys(caseData).forEach((key) => {
        const _key = key as keyof FormValues;
        if (caseData[_key] === null || caseData[_key] === '') {
          delete caseData[_key];
        }
      });

      const response = await postCase(modelCaseData);
      if ('case' in response) {
        const caseId = response.case;
        toast_success(t('case-created'));
        void refresh();
        setNewCaseId(caseId);
      } else if ('Error' in response) {
        toast_error(`${ERROR_CREATING_CASE} ${ERROR_MESSAGE_TRY_AGAIN_EMAIL}`);
      }
    },
    [profile.email, setNewCaseId, t]
  );

  const onSubmit = async (data: FormValues) => {
    // Editing flow: allow editing only if case hasn't been submitted/resulted yet
    if (isEditing && caseData) {
      const resultCode = Number(caseData.result);
      const canEdit =
        resultCode === CASE_STATUS.NOT_SUBMITTED || resultCode === CASE_STATUS.NOT_STARTED;

      if (!canEdit) {
        // If not editable, just continue to image upload
        setNewCaseId(caseData.case_id);
        return;
      }

      try {
        setIsSaving(true);
        // Build payload similar to create but partial
        const formattedBirthday = fromInputDateToFormattedString(data.birthday);
        const patientName = `${data.firstName.trim()} ${data.lastName.trim()}`;
        const patientId = `${patientName}_${formattedBirthday}`;

        const payload = {
          patient_id: patientId,
          site_id: data.siteId,
          camera_manufacturer_model: data.cameraDDL,
          indications: data.indications,
        };

        await patchCase(caseData.case_id, payload);
        toast_success(t('case-updated', 'Case updated'));
        setIsSaving(false);
        // Navigate to image upload to allow attaching images
        setNewCaseId(caseData.case_id);
        void refresh();
        return;
      } catch (error) {
        console.error('Error updating case:', error);
        toast_error(`${ERROR_CREATING_CASE} ${ERROR_MESSAGE_TRY_AGAIN_EMAIL}`);
        setIsSaving(false);
        return;
      }
    }

    // Creation flow
    try {
      setIsSaving(true);
      await createCase(data);
      reset();
      setIsSaving(false);
    } catch (error) {
      console.error('Error creating case:', error);
      toast_error(`${ERROR_CREATING_CASE} ${ERROR_MESSAGE_TRY_AGAIN_EMAIL}`);
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (profile) {
      setValue('createdBy', profile?.email ?? '');
      setValue('clinicId', profile?.clinic_name ?? '');
    }
  }, [profile, setValue]);

  useEffect(() => {
    if (site?.default_camera) {
      setValue('cameraDDL', site?.default_camera);
    }
  }, [site, setValue]);

  // Populate form with case data when editing
  useEffect(() => {
    if (isEditing && caseData) {
      setValue('createdBy', caseData.user_email);
      setValue('clinicId', profile?.clinic_name ?? ''); // Keep current clinic name
      setValue('siteId', caseData.site_id);
      setValue('cameraDDL', caseData.camera_manufacturer_model);

      // Parse patient_id to extract name and birthday
      // Format: "John Doe_01-20-1990"
      const patientParts = caseData.patient_id.split('_');
      if (patientParts.length === 2) {
        const [fullName, birthday] = patientParts;
        const nameParts = fullName.split(' ');
        if (nameParts.length >= 2) {
          setValue('firstName', nameParts[0]);
          setValue('lastName', nameParts.slice(1).join(' '));
        }
        // Convert MM-DD-YYYY to YYYY-MM-DD for date input
        const dateParts = birthday.split('-');
        if (dateParts.length === 3) {
          const [month, day, year] = dateParts;
          setValue('birthday', `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
        }
      }

      setValue('indications', caseData.indications ?? []);
    }
  }, [isEditing, caseData, setValue, profile?.clinic_name]);

  if (!profile) {
    return <FetchStates loading />;
  }

  // Show loading state when fetching case data for editing
  if (isEditing && loadingCase) {
    return <FetchStates loading />;
  }

  // Show error state when case data fails to load
  if (isEditing && caseError) {
    return (
      <FetchStates
        error={caseError?.message ?? 'Failed to load case data'}
        refetch={() => void refetch()}
      />
    );
  }

  // Determine editability for UI controls
  const resultCode = isEditing && caseData ? Number(caseData.result) : null;
  const canEdit = !isCaseSubmitted(resultCode);

  return (
    <>
      <h2 className="title mb-4">
        {isEditing
          ? t('case-detail-title', 'Case Details')
          : t('case-create-title', 'Create a New Case')}
      </h2>
      {loadingSite ? (
        <LoadingDots />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="lg:col-span-2">
              <InputField
                disabled
                required
                label={t('case-create-created-by')}
                {...register('createdBy')}
              />
            </div>
            <InputField
              disabled
              required
              label={t('case-create-clinic')}
              {...register('clinicId')}
            />
            <SelectField
              required
              disabled={(isEditing && !canEdit) || isSaving || loading || loadingSite}
              enableEmptyOption
              label={t('case-create-site')}
              options={catalogs?.sites ?? []}
              errorMessage={errors.siteId?.message}
              {...register('siteId')}
            />
            <SelectField
              required
              disabled={(isEditing && !canEdit) || isSaving || loading || loadingSite}
              enableEmptyOption
              label={t('case-create-camera')}
              options={catalogs?.cams ?? []}
              errorMessage={errors.cameraDDL?.message}
              {...register('cameraDDL')}
            />
          </div>
          <div className="mt-4 lg:mt-8">
            <h3 className="subtitle">{t('case-create-indications')}</h3>
          </div>
          <CaseIndicationMultiSelect
            selected={watch('indications')}
            onSelect={(indications) => setValue('indications', indications)}
            siteId={watch('siteId')}
            disabled={(isEditing && !canEdit) || isSaving || loading}
          />
          <div className="mt-4 lg:mt-8">
            <h3 className="subtitle mb-4">{t('case-create-patient-info')}</h3>
          </div>
          <div className="grid lg:grid-cols-2 gap-4">
            <InputField
              required
              disabled={(isEditing && !canEdit) || isSaving || loading}
              label={t('case-create-patient-first-name')}
              errorMessage={errors.firstName?.message}
              {...register('firstName')}
            />
            <InputField
              required
              disabled={(isEditing && !canEdit) || isSaving || loading}
              label={t('case-create-patient-last-name')}
              errorMessage={errors.lastName?.message}
              {...register('lastName')}
            />
            <InputField
              required
              disabled={(isEditing && !canEdit) || isSaving || loading}
              type="date"
              label={t('case-create-patient-birthday')}
              min="1900-01-01"
              max={new Date().toISOString().split('T')[0]}
              errorMessage={errors.birthday?.message}
              {...register('birthday')}
            />
          </div>

          <div className="mt-8">
            <Button type="submit" loading={isSaving} disabled={loading}>
              {isEditing
                ? t('case-detail-continue-button', 'Continue to Image Upload')
                : t('case-create-continue', 'Continue')}
            </Button>
          </div>
        </form>
      )}
    </>
  );
}
